import React, { useState, useEffect, useCallback } from 'react';
import KycVerification from '../components/KycVerification';
import './pages.css';

function KycPage() {
    const [user, setUser] = useState({ id: '', name: '', kycStatus: '', panNumber: '', phone: '', email: '', aadhaar: null });
    const [isLoading, setIsLoading] = useState(true);
    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchProfile = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (!session?.userId) return setIsLoading(false);
        setIsLoading(true);
        fetch(`${API_URL}/api/auth/profile/${session.userId}`, {
            headers: {
                'Authorization': `Bearer ${session.token}`
            }
        })
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    const pennyDrop = data.kycRecords?.pennyDrop || {};
                    const salaryProfile = data.kycRecords?.salaryProfile || {};
                    const aadhaar = data.kycRecords?.aadhaar || {};
                    setUser({
                        id: data.user.id,
                        name: data.user.name || aadhaar.name || '',
                        kycStatus: data.user.kycStatus,
                        panNumber: data.user.panNumber || data.user.pan_number || '',
                        phone: data.user.phone || '',
                        email: data.user.email || '',
                        bankAccountNumber: pennyDrop.accountNumber || '',
                        bankIfscCode: pennyDrop.ifscCode || '',
                        bankBeneficiaryName: pennyDrop.beneficiaryName || '',
                        income: salaryProfile.accountLast4 ? salaryProfile.accountLast4 : '',
                        employer: salaryProfile.bankName || '',
                        salaryPan: salaryProfile.panNumber || '',
                        aadhaar: aadhaar || null,
                        manualSalaryStatement: data.kycRecords?.manualSalaryStatement || null
                    });
                }
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, [API_URL]);

    useEffect(() => { 
        if (window.opener && window.opener !== window) {
            window.close();
            return;
        }
        fetchProfile(); 
    }, [fetchProfile]);

    const kycSteps = [
        { label: 'Identity Verification', desc: 'PAN & Aadhaar linked check', pts: 200 },
        { label: 'Document Upload', desc: 'Upload your KYC documents', pts: 200 },
        { label: 'Photo & Biometric', desc: 'Selfie and liveness check', pts: 150 },
        { label: 'Review & Verification', desc: 'Team reviews your submission', pts: 50 },
    ];

    const isVerified = user.kycStatus === 'completed';
    const isInProgress = user.kycStatus === 'in_progress';

    if (isLoading && !user.id) return (
        <div className="pg-loading"><div className="pg-spinner" /><span>Loading verification details…</span></div>
    );

    return (
        <div className="pg-root">
            <div className="pg-header">
                <div className="pg-header-text">
                    <h1>🆔 Identity Verification</h1>
                    <p>Complete your KYC to unlock all features and earn reward points</p>
                </div>
                <span className={`pg-badge ${isVerified ? 'pg-badge-green' : isInProgress ? 'pg-badge-blue' : 'pg-badge-amber'}`} style={{ fontSize: '0.82rem', padding: '0.4rem 1rem' }}>
                    {isVerified ? '✅ KYC Verified' : isInProgress ? '🔄 In Progress' : '⏳ Pending'}
                </span>
            </div>

            {/* KYC Steps overview */}
            <div className="kyc-page-layout">
                <div className="pg-panel">
                    <div className="pg-panel-header"><h2>Verification Process</h2></div>
                    <KycVerification
                        userId={user.id}
                        summary={{
                            name: user.name,
                            kycStatus: user.kycStatus,
                            panNumber: user.panNumber,
                            bankAccountNumber: user.bankAccountNumber,
                            bankIfscCode: user.bankIfscCode,
                            income: user.income,
                            employer: user.employer,
                            loanAmount: '',
                            aadhaar: user.aadhaar || {},
                            manualSalaryStatement: user.manualSalaryStatement || null
                        }}
                        onComplete={() => { console.log('KYC done'); setTimeout(fetchProfile, 2000); }}
                    />
                </div>

                <div className="kyc-page-sidebar">
                    <div className="pg-panel">
                        <div className="pg-panel-header"><h2>Journey Steps</h2><span className="pg-panel-badge">+600 pts total</span></div>
                        <div className="pg-kyc-steps">
                            {kycSteps.map((step, i) => {
                                const done = isVerified || (isInProgress && i < 2);
                                const active = !isVerified && isInProgress && i === 2;
                                return (
                                    <div key={step.label} className="pg-kyc-step">
                                        <div className={`pg-kyc-dot ${done ? 'done' : active ? 'active' : ''}`}>
                                            {done ? '✓' : i + 1}
                                        </div>
                                        <div className="pg-kyc-info">
                                            <h4>{step.label}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                        <span className="pg-kyc-pts">+{step.pts} pts</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {isVerified && (
                        <div className="pg-alert pg-alert-success">
                            <span className="pg-alert-icon">🎉</span>
                            <div className="pg-alert-body">
                                <h4>You're Verified!</h4>
                                <p>Your KYC is complete. You can now apply for loans.</p>
                            </div>
                        </div>
                    )}

                    <div className="pg-panel" style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Why Complete KYC?</div>
                        <div className="kyc-benefits-list">
                            {['Access all loan products','Earn 600 bonus reward points','Faster loan approval','Higher loan amounts available'].map(b => (
                                <div className="kyc-benefit-item" key={b} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#334155' }}>
                                    <span style={{ color: '#a5761e', fontWeight: '700' }}>✦</span> {b}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default KycPage;
